// Portal cookie scheme — два слоя.
//
// Слой 1: identity (кто). `cs_portal_<portalId>` → IPortalAuthData
//   ключуется ТОЛЬКО на portalId (не на companyId), т.к. портал = единица
//   видимости для пользователя. email/name тут как cache для UI без лишнего GET.
//
// Слой 2: per-course session ref. `cs_session_<portalId>_<courseId>_<identityKind>_<identityShort>`
//   → ISessionCookieData. Куку identity-key, чтобы при логине под другим email
//   в том же браузере не подсасывалась чужая сессия (БАГ-4099).
//
// Anon-кейс: identity bootstrap-ится в IPortalAuthData.anonSessionId (uuid) —
// тогда `kind=anon, short=<full uuid>`, чтобы анонимная сессия переживала
// retry/reopen в том же браузере, но не делилась с другим пользователем.

const PORTAL_AUTH_PREFIX = 'cs_portal_'
const SESSION_PREFIX = 'cs_session_'
const COOKIE_TTL_DAYS = 30

const cookieSecureAttr = (): string =>
  typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : ''

const writeCookie = (name: string, value: string): void => {
  const expires = new Date(Date.now() + COOKIE_TTL_DAYS * 24 * 60 * 60 * 1000).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${cookieSecureAttr()}`
}

const clearCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

const readCookie = (name: string): string | null => {
  const match = document.cookie.match(
    new RegExp(`(^| )${name.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&')}=([^;]+)`),
  )
  return match ? decodeURIComponent(match[2]) : null
}

// ========== Portal auth (identity) ==========

export interface IPortalAuthData {
  email?: string
  name?: string
  verified: boolean
  ssoAuthenticated?: boolean
  // Identity sources — заполняется одним из трёх: BE → endUserId на верифицированный
  // email, kuserId для парта-юзеров, anonSessionId — bootstrap-нутый uuid для анона.
  endUserId?: string
  kuserId?: string
  anonSessionId?: string
}

const portalAuthName = (portalId: string): string => `${PORTAL_AUTH_PREFIX}${portalId}`

export function getPortalAuthCookie(portalId: string): IPortalAuthData | null {
  const raw = readCookie(portalAuthName(portalId))
  if (!raw) {
    return null
  }
  try {
    return JSON.parse(raw) as IPortalAuthData
  } catch {
    return null
  }
}

export function setPortalAuthCookie(portalId: string, data: IPortalAuthData): void {
  writeCookie(portalAuthName(portalId), JSON.stringify(data))
}

export function clearPortalAuthCookie(portalId: string): void {
  clearCookie(portalAuthName(portalId))
}

// ========== Identity key (scope per-course session cookies) ==========

export type IdentityKind = 'u' | 'k' | 'anon'

export interface IIdentityKey {
  kind: IdentityKind
  // Для `u`/`k` — первые 8 hex от endUserId/kuserId. Для `anon` — full anonSessionId
  // uuid (он же — owner-check value на BE без extra-lookup).
  short: string
}

const shortHex = (uuid: string): string => uuid.replace(/-/g, '').slice(0, 8)

export function identityKeyFromAuth(auth: IPortalAuthData | null): IIdentityKey | null {
  if (!auth) {
    return null
  }
  if (auth.endUserId) {
    return { kind: 'u', short: shortHex(auth.endUserId) }
  }
  if (auth.kuserId) {
    return { kind: 'k', short: shortHex(auth.kuserId) }
  }
  if (auth.anonSessionId) {
    return { kind: 'anon', short: auth.anonSessionId }
  }
  return null
}

// ========== Per-course session ref ==========

export interface ISessionCookieData {
  sessionId: string
  sessionToken?: string
}

const sessionCookieName = (portalId: string, courseId: string, identity: IIdentityKey): string =>
  `${SESSION_PREFIX}${portalId}_${courseId}_${identity.kind}_${identity.short}`

export function getSessionCookie(
  portalId: string,
  courseId: string,
  identity: IIdentityKey,
): ISessionCookieData | null {
  const raw = readCookie(sessionCookieName(portalId, courseId, identity))
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && typeof parsed.sessionId === 'string') {
      return { sessionId: parsed.sessionId, sessionToken: parsed.sessionToken }
    }
  } catch {
    return null
  }
  return null
}

export function setSessionCookie(
  portalId: string,
  courseId: string,
  identity: IIdentityKey,
  data: ISessionCookieData,
): void {
  writeCookie(sessionCookieName(portalId, courseId, identity), JSON.stringify(data))
}

export function clearSessionCookie(
  portalId: string,
  courseId: string,
  identity: IIdentityKey,
): void {
  clearCookie(sessionCookieName(portalId, courseId, identity))
}

// ========== Logout ==========

// Сносит identity-cookie + ВСЕ `cs_session_<portalId>_*` для портала, чтобы
// после "switch user" следующий вход не подсосал session-ref предыдущего юзера
// через identity-keyed куку (которая после смены identity станет недостижима,
// но будет вечно занимать место и протекать в HAR при отладке).
export function clearPortalSession(portalId: string): void {
  const portalKey = portalAuthName(portalId)
  const sessionPrefix = `${SESSION_PREFIX}${portalId}_`
  const cookies = document.cookie.split(';')
  for (const raw of cookies) {
    const eq = raw.indexOf('=')
    const name = (eq === -1 ? raw : raw.slice(0, eq)).trim()
    if (name === portalKey || name.startsWith(sessionPrefix)) {
      clearCookie(name)
    }
  }
}
